import { Injectable, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

export type ConfirmSeverity = 'danger' | 'primary';

export interface ConfirmOptions {
    message: string;
    header?: string;
    icon?: string;
    acceptLabel?: string;
    rejectLabel?: string;
    severity?: ConfirmSeverity;
    accept: () => void;
    reject?: () => void;
}

/** Opciones de `confirmDelete`: el mensaje y la acción van como argumentos; la severidad ya es `danger`. */
export type ConfirmDeleteOptions = Partial<Omit<ConfirmOptions, 'message' | 'accept' | 'severity'>>;

/** Única fuente de los textos/iconos de los diálogos de confirmación. */
export const CONFIRM_DEFAULTS = {
    header: 'Confirmar',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Confirmar',
    rejectLabel: 'Cancelar',
    severity: 'primary' as ConfirmSeverity,
    delete: {
        header: 'Eliminar',
        acceptLabel: 'Sí, eliminar',
        severity: 'danger' as ConfirmSeverity,
    },
} as const;

/** Clase de PrimeNG del botón de aceptar según la severidad (`primary` = la del tema). */
const ACCEPT_STYLE_CLASS: Readonly<Record<ConfirmSeverity, string>> = {
    danger: 'p-button-danger',
    primary: '',
};

/** Ancho único del diálogo de confirmación (una sola instancia de `<p-confirmdialog>` en el layout). */
export const CONFIRM_DIALOG_STYLE = { width: '30rem', maxWidth: '96vw' } as const;

/**
 * Wrapper único sobre `ConfirmationService` de PrimeNG. Los componentes NO inyectan
 * `ConfirmationService` ni escriben iconos/etiquetas: llaman a `confirm` o `confirmDelete`.
 * El diálogo se renderiza una sola vez en `MainLayoutComponent`.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
    private readonly confirmationService = inject(ConfirmationService);

    confirm(options: ConfirmOptions): void {
        const severity = options.severity ?? CONFIRM_DEFAULTS.severity;
        this.confirmationService.confirm({
            message: options.message,
            header: options.header ?? CONFIRM_DEFAULTS.header,
            icon: options.icon ?? CONFIRM_DEFAULTS.icon,
            acceptLabel: options.acceptLabel ?? CONFIRM_DEFAULTS.acceptLabel,
            rejectLabel: options.rejectLabel ?? CONFIRM_DEFAULTS.rejectLabel,
            acceptButtonStyleClass: ACCEPT_STYLE_CLASS[severity],
            accept: options.accept,
            reject: options.reject,
        });
    }

    confirmDelete(message: string, accept: () => void, options: ConfirmDeleteOptions = {}): void {
        this.confirm({
            header: CONFIRM_DEFAULTS.delete.header,
            acceptLabel: CONFIRM_DEFAULTS.delete.acceptLabel,
            ...options,
            severity: CONFIRM_DEFAULTS.delete.severity,
            message,
            accept,
        });
    }
}
